import type { ComponentType, ReactNode } from "react";

const TREND_COLORS = {
  negative: "#ef4444",
  positive: "#10b981",
} as const;

const TREND_PATHS = {
  down: {
    line: "M0 8 L28 10 L56 18 L84 26 L112 34 L140 40 L160 44",
    area: "M0 8 L28 10 L56 18 L84 26 L112 34 L140 40 L160 44 L160 48 L0 48 Z",
  },
  up: {
    line: "M0 40 L28 38 L56 30 L84 22 L112 14 L140 8 L160 4",
    area: "M0 40 L28 38 L56 30 L84 22 L112 14 L140 8 L160 4 L160 48 L0 48 Z",
  },
} as const;

export function DashboardStatMetric({
  children,
  tone = "negative",
  icon: Icon,
  centered = false,
}: {
  children: ReactNode;
  tone?: keyof typeof TREND_COLORS | "neutral";
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  centered?: boolean;
}) {
  const colorClass =
    tone === "negative"
      ? "text-red-500"
      : tone === "neutral"
        ? "text-[#1C1C1C]"
        : "text-emerald-500";

  return (
    <p
      className={`mt-1 flex items-center gap-1.5 text-xl font-bold tracking-tight ${colorClass} xl:mt-1.5 xl:text-2xl ${
        centered ? "w-full justify-center" : ""
      }`}
    >
      {children}
      {Icon ? <Icon className="text-base text-zinc-400 xl:text-lg" aria-hidden /> : null}
    </p>
  );
}

export function DashboardAreaLineChart({
  id,
  direction,
  tone = "negative",
}: {
  id: string;
  direction: keyof typeof TREND_PATHS;
  tone?: keyof typeof TREND_COLORS;
}) {
  const color = TREND_COLORS[tone];
  const { line, area } = TREND_PATHS[direction];

  return (
    <svg
      viewBox="0 0 160 48"
      className="mt-2 h-10 w-full xl:mt-3 xl:h-12"
      aria-hidden
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
